/**
 * Canonical visual mapping for an effective presence status.
 *
 * Mirrors the colours used in <PresenceStatusPicker> so a user's own picker and
 * the way others see them stay consistent. Watcher-facing surfaces (chat list,
 * conversation header, profile panel) key off these instead of a bare
 * `status === 'online'` boolean — otherwise away/busy/dnd render identically to
 * offline and only "online" is ever reflected.
 */

import type { PresenceStatus } from '../store/presence.store'

/** Tailwind bg-class for the status dot. `null` → render no dot (offline). */
export const statusDotClass: Record<PresenceStatus, string | null> = {
  online:  'bg-green-500',
  away:    'bg-amber-500',
  busy:    'bg-rose-500',
  dnd:     'bg-purple-500',
  offline: null,
}

/** Tailwind text-class for the status label. */
export const statusTextClass: Record<PresenceStatus, string> = {
  online:  'text-green-500',
  away:    'text-amber-500',
  busy:    'text-rose-500',
  dnd:     'text-purple-500',
  offline: 'text-muted-foreground',
}

/** i18n key for the status label (see clientWeb/i18n/translations/all-translations.json → presence.*). */
export const statusLabelKey: Record<PresenceStatus, string> = {
  online:  'presence.online',
  away:    'presence.statusAway',
  busy:    'presence.statusBusy',
  dnd:     'presence.statusDoNotDisturb',
  // "Offline", not the old "Last seen recently", which claimed a time nobody had.
  offline: 'presence.statusOffline',
}
